import { ReactElement, useCallback } from 'react'
import { useIsOrganisationAdmin } from '@hooks'
import { AxiosError } from 'axios'
import type { ShearConnectionDTO, ShearConnectionPrioritizationDTO } from '@api/fastAPI.schemas'
import {
  getGetOrganisationShearConnectionPrioritizationQueryKey,
  useGetOrganisationShearConnectionPrioritization,
  useSetOrganisationShearConnectionPrioritization,
} from '@api/organisation-shear-connection-prioritization/organisation-shear-connection-prioritization'
import {
  getAllShearConnectionsQueryKey,
  useAddShearConnection,
  useAllShearConnections,
  useDeleteShearConnection,
  usePostUpdateShearConnection,
} from '@api/shear-connections/shear-connections'
import { useQueryClient } from '@tanstack/react-query'
import { LoadingIndicator } from '@ui/feedback'
import { useSnackbar } from '@ui/snackbar'
import { buildErrorMessage } from 'src/constants/errors'
import ShearConnectionList from '../ShearConnectionList'
import ShearConnectionPrioritizationLists from '../ShearConnectionPrioritizationLists'
import { toShearConnectionCreate } from '../constants'

/** The organisation's Schubverbinder library, plus its prioritization per storey category. */
const OrganisationShearConnectionManager = (): ReactElement => {
  const queryClient = useQueryClient()
  const { enqueueSnackbar } = useSnackbar()
  const { isOrganisationAdmin } = useIsOrganisationAdmin()

  const invalidateConnections = useCallback(
    () => queryClient.invalidateQueries(getAllShearConnectionsQueryKey()),
    [queryClient],
  )
  const invalidatePrioritization = useCallback(
    () => queryClient.invalidateQueries(getGetOrganisationShearConnectionPrioritizationQueryKey()),
    [queryClient],
  )

  const { data: connections, isLoading } = useAllShearConnections<ShearConnectionDTO[], AxiosError>(
    {
      query: {
        onError: error => {
          enqueueSnackbar(buildErrorMessage(error, 'Fehler beim Laden der Schubverbinder'), {
            variant: 'error',
          })
        },
      },
    },
  )

  const { data: prioritization, isLoading: isLoadingPrioritization } =
    useGetOrganisationShearConnectionPrioritization<ShearConnectionPrioritizationDTO, AxiosError>({
      query: {
        onError: error => {
          enqueueSnackbar(
            buildErrorMessage(error, 'Fehler beim Laden der Schubverbinderpriorisierung'),
            { variant: 'error' },
          )
        },
      },
    })

  const { mutateAsync: addConnection, isLoading: isCreating } = useAddShearConnection<
    AxiosError,
    unknown
  >({
    mutation: {
      meta: {
        title: 'Schubverbinder anlegen',
        description: 'Legt einen Schubverbinder in der Organisationsbibliothek an',
      },
      onSuccess: async () => {
        await invalidateConnections()
        enqueueSnackbar('Schubverbinder erfolgreich angelegt', { variant: 'success' })
      },
      onError: error => {
        enqueueSnackbar(buildErrorMessage(error, 'Fehler beim Speichern des Schubverbinders'), {
          variant: 'error',
        })
      },
    },
  })

  const { mutateAsync: updateConnection, isLoading: isEditing } = usePostUpdateShearConnection<
    AxiosError,
    unknown
  >({
    mutation: {
      meta: {
        title: 'Schubverbinder speichern',
        description: 'Speichert die Änderungen an einem Schubverbinder',
      },
      onSuccess: async () => {
        await invalidateConnections()
        enqueueSnackbar('Schubverbinder erfolgreich gespeichert', { variant: 'success' })
      },
      onError: error => {
        enqueueSnackbar(buildErrorMessage(error, 'Fehler beim Speichern des Schubverbinders'), {
          variant: 'error',
        })
      },
    },
  })

  const { mutate: deleteConnection } = useDeleteShearConnection<AxiosError, unknown>({
    mutation: {
      meta: {
        title: 'Schubverbinder löschen',
        description: 'Löscht einen Schubverbinder aus der Organisationsbibliothek',
      },
      onSuccess: async () => {
        await invalidateConnections()
        // Deleting a connector also drops it from every prioritization list.
        await invalidatePrioritization()
        enqueueSnackbar('Schubverbinder erfolgreich gelöscht', { variant: 'success' })
      },
      onError: error => {
        enqueueSnackbar(buildErrorMessage(error, 'Fehler beim Löschen des Schubverbinders'), {
          variant: 'error',
        })
      },
    },
  })

  const { mutate: savePrioritization } = useSetOrganisationShearConnectionPrioritization<
    AxiosError,
    unknown
  >({
    mutation: {
      meta: {
        title: 'Schubverbinderpriorisierung speichern',
        description: 'Speichert die Reihenfolge der Schubverbinder pro Geschosskategorie',
      },
      onSuccess: async () => {
        await invalidatePrioritization()
        enqueueSnackbar('Schubverbinderpriorisierung erfolgreich gespeichert', {
          variant: 'success',
        })
      },
      onError: error => {
        enqueueSnackbar(
          buildErrorMessage(error, 'Fehler beim Speichern der Schubverbinderpriorisierung'),
          { variant: 'error' },
        )
      },
    },
  })

  if (isLoading || isLoadingPrioritization || !connections || !prioritization) {
    return <LoadingIndicator />
  }

  return (
    <>
      <ShearConnectionList
        connections={connections}
        isCreating={isCreating}
        isEditing={isEditing}
        onCreate={values => addConnection({ data: toShearConnectionCreate(values) })}
        onEdit={values =>
          updateConnection({ connectionId: values.guid, data: toShearConnectionCreate(values) })
        }
        onDelete={({ guid }) => deleteConnection({ connectionId: guid })}
      />
      {isOrganisationAdmin && (
        <ShearConnectionPrioritizationLists
          connections={connections}
          value={prioritization}
          onChange={next => savePrioritization({ data: next })}
        />
      )}
    </>
  )
}

export default OrganisationShearConnectionManager
